import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import {
  isPendingAction,
  isRejectedAction,
  isFulfilledAction
} from './actions';

const name = 'user';

export const login = createAsyncThunk(
  `${name}/login`,
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/auth/login/local', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUser = createAsyncThunk(
  `${name}/getUser`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/auth/user`, {
        params: {
          t: new Date().getTime()
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({ notify: false, ...error.response.data });
    }
  }
);

export const changePassword = createAsyncThunk(
  `${name}/changePassword`,
  async (data, { rejectWithValue }) => {
    try {
      await axios.post('/api/auth/user/change-password', data);
      return true;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const changeSessionTenant = createAsyncThunk(
  `${name}/changeSessionTenant`,
  async (tenantId) => {
    try {
      const response = await axios.get(`/api/auth/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      return false;
    }
  }
);

const refreshUser = (state, action) => {
  const user = action?.payload;

  if (!user) {
    state.isLoggedIn = false;
    return;
  }

  if (user?.roles) {
    if (user?.roles?.some((role) => role.level === 0)) {
      state.role = 'root';
    } else {
      if (state.selectedRole) {
        state.role = state.selectedRole;
      } else {
        state.role = user.tenants[
          user.tenants.findIndex(
            (element) => element.id === user?.selectedTenant
          )
        ]?.roles?.find((role) => role.level).key;
      }
    }
  }

  state.isLoggedIn = true;
  state.user = user;
};

const resetUser = (state) => {
  window.localStorage.removeItem('access-token');
  window.localStorage.removeItem('tenant');
  axios.defaults.headers.common['Auth'] = null;
  state.errors = false;
  state.user = null;
  state.role = null;
  state.isLoggedIn = false;
  state.currentTenant = null;
  state.tenantId = null;
};

const authSlice = createSlice({
  name,
  initialState: {
    loading: true,
    errors: false,
    user: null,
    role: null,
    isLoggedIn: false,
    selectedRole: null,
    currentTenant: null,
    tenantId: null
  },
  reducers: {
    selectRole: (state, action) => {
      state.role = action.payload.key;
    },
    updateUserTenant: (state, action) => {
      const { payload } = action;
      let updatedTenants;

      if (state.user.tenants?.some((t) => t.id === payload.id)) {
        // update existing user.tenant
        updatedTenants = (state.user.tenants || []).map((t) =>
          t.id === payload.id
            ? {
                ...t,
                ...payload
              }
            : t
        );
      } else {
        // add new user.tenant
        updatedTenants = (state.user.tenants || []).concat({
          roles: [],
          ...payload
        });
      }

      state.user = {
        ...state.user,
        tenants: updatedTenants
      };
    },
    logout: () => {
      window.location = '/authentication/logout';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addCase(getUser.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addCase(changeSessionTenant.fulfilled, (state, action) => {
        refreshUser(state, action);
      })
      .addMatcher(isFulfilledAction(name), (state) => {
        state.loading = false;
      })
      .addMatcher(isPendingAction(name), (state) => {
        state.loading = true;
        state.errors = false;
      })
      .addMatcher(isRejectedAction(name), (state, action) => {
        if (action.type !== `${name}/changePassword/rejected`) {
          resetUser(state, action);
        }
        state.loading = false;
        state.errors = action.payload;
      });
  }
});

export const { selectRole, currentTenant, tenantId, updateUserTenant, logout } =
  authSlice.actions;
export default authSlice.reducer;
